import type { DifficultyVariant, FixtureCellData } from '../constants';
import { ratingToDifficulty } from '../utils';
import type { FixtureValueMetric } from '../values';

// The refreshed palette: the existing pastel cells deepened slightly and given
// a left bar and a text colour, so the scale reads without relying on hue alone.
export type DifficultyBand = {
  background: string;
  bar: string;
  text: string;
};

export const DIFFICULTY_BANDS: Record<DifficultyVariant, DifficultyBand> = {
  'very-easy': { background: '#C9E4DB', bar: '#088167', text: '#065145' },
  easy: { background: '#E4F1EC', bar: '#3FB299', text: '#0A6B57' },
  moderate: { background: '#F1EDE6', bar: '#B7B7B9', text: '#5C5C5D' },
  hard: { background: '#FBEAF0', bar: '#E4568A', text: '#A21D4E' },
  'very-hard': { background: '#F7DAE4', bar: '#CC0041', text: '#8E0F35' }
};

// The legend keeps the original five pastels, ordered harder to easier to
// match the "HARDER ... EASIER" labels either side of it.
export const LEGEND_SWATCHES: readonly string[] = [
  '#F2BFCF',
  '#F7D9E3',
  '#F1EDE6',
  '#DAECE8',
  '#A9D3CA'
];

const MODERATE_DIFFICULTY = 5;

// Cells are coloured by the difficulty that matches the selected metric, so
// switching to "Attack" recolours the grid by attacking difficulty. Goals and
// clean sheets have no difficulty of their own and fall back to overall.
const getBandDifficulty = (
  fixture: FixtureCellData,
  metric: FixtureValueMetric
): number => {
  const difficulty = fixture.inferences?.difficulty;
  if (!difficulty) return MODERATE_DIFFICULTY;
  switch (metric) {
    case 'attack':
    case 'defence':
      return difficulty[metric];
    default:
      return difficulty.overall;
  }
};

export const getFixtureBand = (
  fixture: FixtureCellData,
  metric: FixtureValueMetric
): DifficultyBand =>
  DIFFICULTY_BANDS[ratingToDifficulty(getBandDifficulty(fixture, metric))];
